import { findEventEntry, type Chapter } from "@repo/vihangam-yog-shared";

import { CHAPTER } from "../../chapter";

import { kundUnitsOf, splitKundsOf, tierOf, tierQuantityOf } from "./kunds";
import { isNotComing } from "./notComing";
import {
  formatMoney,
  type AccessCounts,
  type EventGroup,
  type Registration,
} from "./shared";

/**
 * The words and numbers the Events page prints about guests: people
 * counts, arrival, how a place was paid for, the Kund tally. Pure, so the
 * wording is unit-tested apart from the components.
 */

/** "1 person" / "24 people". */
export function peopleCount(n: number): string {
  return `${n} ${n === 1 ? "person" : "people"}`;
}

/** "1 booking" / "14 bookings". */
function bookingCount(n: number): string {
  return `${n} ${n === 1 ? "booking" : "bookings"}`;
}

/** The meta line under an event in the list: "24 people · 14 bookings". */
export function eventMetaLine(people: number, bookings: number): string {
  return bookings === 0
    ? "No bookings yet"
    : `${peopleCount(people)} · ${bookingCount(bookings)}`;
}

/** "18 adults, 6 children"; empty parts are left out. */
export function adultsAndChildren(adults: number, children: number): string {
  const parts: string[] = [];
  if (adults > 0) {
    parts.push(`${adults} ${adults === 1 ? "adult" : "adults"}`);
  }
  if (children > 0) {
    parts.push(`${children} ${children === 1 ? "child" : "children"}`);
  }
  return parts.join(", ");
}

/** "3 people (2 adults, 1 child)". */
export function partySizeLabel(
  r: Pick<Registration, "totalPeople" | "adults" | "children">,
): string {
  const split = adultsAndChildren(r.adults, r.children);
  return split
    ? `${peopleCount(r.totalPeople)} (${split})`
    : peopleCount(r.totalPeople);
}

/** "1 Sep, 9:05 am": no leading zeros. */
export function signedUpLabel(iso: string): string {
  return new Date(iso).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "9:05 am". */
export function clockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-AU", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** How far through the door one booking is. */
type ArrivalState =
  | { kind: "arrived"; at: string }
  | { kind: "partly"; here: number; of: number }
  | { kind: "not-yet" };

export function arrivalOf(
  r: Pick<Registration, "checkedInAt" | "party">,
): ArrivalState {
  if (r.checkedInAt) {
    return { kind: "arrived", at: r.checkedInAt };
  }
  const here = r.party.filter((m) => m.arrivedAt).length;
  return here > 0
    ? { kind: "partly", here, of: r.party.length }
    : { kind: "not-yet" };
}

/** Where the per-guest "Check in" link goes: Reception's own deep link,
 *  which opens the guest card and never checks anyone in by itself. */
export function receptionLinkFor(
  r: Pick<Registration, "eventSlug" | "confirmationRef">,
): string {
  return `/reception/${encodeURIComponent(r.eventSlug)}?ref=${encodeURIComponent(r.confirmationRef)}`;
}

/** Since the combined form went live at 20:47 on 9 Sep 2026 (Sydney),
 *  the WhatsApp tick means "please add me". Before, it meant "I have
 *  already joined" (backend bundle.ts, whatsappJoined). */
const WHATSAPP_CONSENT_FROM_MS = Date.parse("2026-09-09T20:47:40+10:00");

/** The WhatsApp tag for a booking that ticked the box; null otherwise. */
export function whatsappLabel(
  r: Pick<Registration, "whatsappOptIn" | "createdAt">,
): "Add to WhatsApp group" | "Already in WhatsApp group" | null {
  if (!r.whatsappOptIn) {
    return null;
  }
  return Date.parse(r.createdAt) >= WHATSAPP_CONSENT_FROM_MS
    ? "Add to WhatsApp group"
    : "Already in WhatsApp group";
}

/** "Card" / "Bank transfer" / "Gifted" / "Discount code"; null when the
 *  row doesn't say (a free booking, or an old hand-added one). */
export function paidByLabel(
  r: Pick<Registration, "paymentMethod" | "receiptUrl" | "bookingType">,
): string | null {
  if (r.bookingType !== "paid") {
    return null;
  }
  switch (r.paymentMethod) {
    case "gifted":
      return "Gifted";
    case "bank_transfer":
      return "Bank transfer";
    case "discount":
      return "Discount code";
    default:
      return r.receiptUrl ? "Card" : null;
  }
}

/**
 * Confirm place records a hand-added place as paid or gifted and emails
 * the guest. A gift zeroes the recorded payment, so it is never offered
 * on a card or discount-code booking. Old hand-added rows carry no
 * payment method and no receipt, so they still get it.
 */
export function canConfirmPlace(
  r: Pick<Registration, "paymentMethod" | "receiptUrl">,
): boolean {
  if (r.paymentMethod === "bank_transfer" || r.paymentMethod === "gifted") {
    return true;
  }
  return r.paymentMethod === null && r.receiptUrl === null;
}

/**
 * One short payment status for the guest list: "Paid $500", "Bank
 * transfer $500", "Gifted", "Discount code", or "Not paid yet" for someone
 * who registered interest. "No payment" for a free booking.
 */
export function paymentStatusLabel(
  r: Pick<
    Registration,
    "bookingType" | "paymentMethod" | "paidAmountCents" | "eventSlug"
  >,
): string {
  if (r.bookingType === "interest") {
    return "Not paid yet";
  }
  if (r.bookingType !== "paid") {
    return "No payment";
  }
  const amount = formatMoney(r.paidAmountCents ?? 0, currencyOf(r.eventSlug));
  switch (r.paymentMethod) {
    case "gifted":
      return "Gifted";
    case "discount":
      return "Discount code";
    case "bank_transfer":
      return `Bank transfer ${amount}`;
    default:
      return `Paid ${amount}`;
  }
}

/** A paid booking whose confirmation email hasn't gone out, and could. */
export function confirmationNotSent(
  r: Pick<
    Registration,
    "bookingType" | "isWalkIn" | "email" | "confirmationSentAt"
  >,
): boolean {
  return (
    r.bookingType === "paid" &&
    !r.isWalkIn &&
    r.email.trim() !== "" &&
    !r.confirmationSentAt
  );
}

/** "1 registered interest but hasn't paid yet" / "3 registered interest
 *  but haven't paid yet". */
export function notPaidYetLine(n: number): string {
  return `${n} registered interest but ${n === 1 ? "hasn't" : "haven't"} paid yet`;
}

/** The currency this event is priced in (Christchurch is NZD in the
 *  Australian build); this build's own when the catalogue has no price. */
export function currencyOf(eventSlug: string): string {
  return findEventEntry(eventSlug)?.price?.currency ?? CHAPTER.currency;
}

/** The type of place a booking bought: "Full Kund", "Shared Kund ×3";
 *  null when the booking doesn't record one. Singapore's catalogue labels
 *  carry the price after a comma ("Your own Kund, S$201 per Kund (per
 *  family, not shared)"); only the name before it is shown. */
export function placeTypeOf(
  r: Pick<
    Registration,
    | "eventSlug"
    | "bookingType"
    | "paymentBreakdown"
    | "paidAmountCents"
    | "totalPeople"
  >,
  chapter: Chapter = CHAPTER.id,
): string | null {
  const label = r.paymentBreakdown?.tierLabel;
  if (!label) {
    return null;
  }
  const comma = label.indexOf(",");
  const catalogueName = comma > 0 ? label.slice(0, comma).trim() : label;
  // Australia no longer splits Kunds: older shared or paired places read
  // as a plain Kund. Singapore still sells shared places per person.
  const name =
    chapter === "aus" && /\b(shared|paired)\b/i.test(catalogueName)
      ? "Kund"
      : catalogueName;
  const quantity = tierQuantityOf(r);
  const place = quantity > 1 ? `${name} ×${quantity}` : name;
  // A group of 6 or more on a Full Kund splits across two.
  const splitKunds = splitKundsOf(r);
  return splitKunds === null ? place : `${place} · ${splitKunds} Kunds`;
}

/** Add-ons and a donation on top of the place, in one line: "Puja Thali
 *  with fruit ×2 · $51 donation"; null when there are none. */
export function extrasOf(
  r: Pick<Registration, "paymentBreakdown" | "eventSlug">,
): string | null {
  const b = r.paymentBreakdown;
  if (!b) {
    return null;
  }
  const parts = b.addOns.map((a) =>
    a.quantity > 1 ? `${a.label} ×${a.quantity}` : a.label,
  );
  if (b.donationCents > 0) {
    parts.push(
      `${formatMoney(b.donationCents, currencyOf(r.eventSlug))} donation`,
    );
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** The "Who is coming" pieces, with plurals: "2 use a wheelchair". */
export function whoIsComingParts(access: AccessCounts): string[] {
  const parts: string[] = [];
  const add = (n: number, one: string, many: string) => {
    if (n > 0) {
      parts.push(`${n} ${n === 1 ? one : many}`);
    }
  };
  add(access.wheelchair, "uses a wheelchair", "use a wheelchair");
  add(access.chair, "needs a chair", "need a chair");
  add(access.infants, "infant", "infants");
  add(access.underTen, "child under 10", "children under 10");
  add(access.seniors, "senior", "seniors");
  add(access.women, "woman", "women");
  add(access.men, "man", "men");
  return parts;
}

/** "1 booking has never done a Hawan" / "2 bookings have never done a Hawan". */
export function firstTimerLine(n: number): string {
  return n === 1
    ? "1 booking has never done a Hawan"
    : `${n} bookings have never done a Hawan`;
}

/** "1 booking is seva and not coming", "2 bookings aren't coming", or both
 *  at once: "3 bookings aren't coming, including 1 seva booking". Null
 *  when every booking is coming. */
export function notComingLine(seva: number, other: number): string | null {
  if (seva > 0 && other > 0) {
    return `${seva + other} bookings aren't coming, including ${seva} seva ${seva === 1 ? "booking" : "bookings"}`;
  }
  if (seva > 0) {
    return seva === 1
      ? "1 booking is seva and not coming"
      : `${seva} bookings are seva and not coming`;
  }
  if (other > 0) {
    return other === 1
      ? "1 booking isn't coming"
      : `${other} bookings aren't coming`;
  }
  return null;
}

/** Kunds booked for a Kund-based Yajna: "13 of 51". */
export interface KundTally {
  booked: number;
  total: number;
}

/** A paid event's catalogue price: its place types, currency and Kunds. */
export type EventPrice = NonNullable<
  NonNullable<ReturnType<typeof findEventEntry>>["price"]
>;

/**
 * How Kunds are counted. Australia no longer shares Kunds, so every paid
 * Kund booking counts as whole Kunds ("whole"), and a group of 6 or more
 * on a Full Kund as the two it splits across. Singapore keeps the
 * catalogue weights ("weighted"), which match its public "Kunds left"
 * counter: a shared place is a fifth of a Kund.
 */
type KundCounting = "whole" | "weighted";

/** Whole Kunds in Australia, weighted Kunds everywhere else. */
export function kundCountingFor(chapter: Chapter): KundCounting {
  return chapter === "aus" ? "whole" : "weighted";
}

export function kundTallyOf(
  registrations: readonly Registration[],
  price: EventPrice | undefined,
  counting: KundCounting,
): KundTally | null {
  if (!price?.kundsTotal) {
    return null;
  }
  let booked = 0;
  for (const r of registrations) {
    // A booking marked not coming (seva) holds no Kund, until someone on
    // it arrives.
    if (r.bookingType !== "paid" || isNotComing(r)) {
      continue;
    }
    const tier = tierOf(r, price);
    const weight = tier?.kunds ?? 0;
    if (weight <= 0) {
      continue;
    }
    // A group of 6 or more on a Full Kund holds the two it splits across.
    const kunds = kundUnitsOf(r, tier);
    booked += counting === "whole" ? kunds : weight * kunds;
  }
  return { booked, total: price.kundsTotal };
}

/** "13", or "13.5" when there is a part Kund. */
export function formatKunds(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
}

/** A catalogue time label without dash punctuation: "10:00 am to 1:30 pm AEST". */
export function plainTimeLabel(label: string): string {
  return label.replace(/\s*[—–]\s*/g, " to ");
}

/** The "date, time" and "venue" lines under an event's title. */
interface EventPlace {
  when: string;
  where: string | null;
}

export function eventWhenAndWhere(group: EventGroup): EventPlace {
  const entry = findEventEntry(group.eventSlug);
  const time = entry?.timeLabel ? plainTimeLabel(entry.timeLabel) : "";
  const when = [group.eventDateLabel, time].filter(Boolean).join(", ");
  const venue = entry?.venue;
  const where = venue
    ? [venue.name, venue.suburb].filter(Boolean).join(", ") || null
    : null;
  return { when, where };
}
